import type { ReactNode } from "react";
import { isString, isObject } from "@huub-web/utils";
import {
  Flex,
  Text,
  type PubSubContextState,
  type SnackbarProps,
} from "@huub-web/ui";

import {
  WebSocketChannel,
  type WebSocketActionMessage,
  type WebSocketChatInitiateMessage,
  type WebSocketChatMessage,
  type WebSocketFeedbackMessage,
  type WebSocketMessage,
} from "../../types";

/**
 * Creates a visual debug function that publishes debug messages as snackbars
 */
export const createVisualDebug =
  (
    debug: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pubSub: PubSubContextState<any>,
  ) =>
  (
    title: string | string[],
    description: ReactNode = "",
    variant: SnackbarProps["variant"] = "warning",
  ) => {
    if (!debug) {
      return;
    }

    pubSub.publish("snackbar", {
      title: `[WebSocket] **${isString(title) ? title : title.join(" ")}**`,
      body: description,
      variant,
    });
  };

/**
 * Formats a channel name for logging with directional indicators
 */
export const formatChannelLog = (
  channel: WebSocketChannel,
  inOut?: "in" | "out",
) => `${inOut ? (inOut === "in" ? "▼ " : "▲ ") : ""}${channel}`;

/**
 * Creates debug body content for outgoing messages
 */
export const createOutgoingMessageDebugBody = (
  channel: WebSocketChannel,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  socketMessage: any,
): ReactNode => {
  if (channel === WebSocketChannel.CHAT_INITIATE) {
    return (
      <Flex gap="xs" align="center" justify="space-between">
        <Text variant="bodyS" bold noWrap>
          Context
        </Text>
        <Text variant="monospace">
          {(socketMessage as WebSocketChatInitiateMessage).data.context?.join(
            " > ",
          )}
        </Text>
      </Flex>
    );
  }

  if (channel === WebSocketChannel.CHAT) {
    return (
      <div style={{ width: "100%", overflow: "hidden" }}>
        {isString(socketMessage.data) ? (
          socketMessage.data
        ) : isObject(socketMessage.data) ? (
          <Flex
            direction="column"
            gap="xxs"
            justify="space-between"
            style={{
              width: "100%",
              overflow: "hidden",
            }}
          >
            <Flex gap="m" align="center" justify="space-between">
              <Text variant="bodyS" bold noWrap>
                Context
              </Text>
              <Text variant="monospace" ellipsis align="right">
                {socketMessage.data?.context?.join(" > ")}
              </Text>
            </Flex>
            {socketMessage.data?.displayPrompt && (
              <Flex gap="m" align="center" justify="space-between">
                <Text variant="bodyS" bold noWrap>
                  Display prompt
                </Text>
                <Text variant="monospace" ellipsis align="right">
                  {socketMessage.data?.displayPrompt}
                </Text>
              </Flex>
            )}
            {socketMessage.data?.prompt && (
              <Flex gap="m" align="center" justify="space-between">
                <Text variant="bodyS" bold noWrap>
                  Prompt
                </Text>
                <Text variant="monospace" ellipsis align="right">
                  {socketMessage.data?.prompt}
                </Text>
              </Flex>
            )}
          </Flex>
        ) : (
          <pre>{JSON.stringify(socketMessage.data, null, 2)}</pre>
        )}
      </div>
    );
  }

  return "";
};

/**
 * Creates debug body content for incoming messages
 */
export const createIncomingMessageDebugBody = (
  channel: WebSocketChannel,
  message: WebSocketMessage,
): ReactNode => {
  if (channel === WebSocketChannel.CHAT) {
    return (
      <Flex direction="column" gap="xs">
        <Flex gap="m" align="center" justify="space-between">
          <Text variant="bodyS" bold noWrap>
            ID
          </Text>
          <Text variant="monospace" ellipsis align="right">
            {(message as WebSocketChatMessage).data.message_id}
          </Text>
        </Flex>
        <Flex gap="m" align="center" justify="space-between">
          <Text variant="bodyS" bold noWrap>
            Message
          </Text>
          <Text variant="monospace" ellipsis align="right">
            {(message as WebSocketChatMessage).data?.message}
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (
    channel === WebSocketChannel.CHAT_FEEDBACK ||
    channel === WebSocketChannel.CHAT_TEMPLATES
  ) {
    return (
      <Flex gap="m" align="center" justify="space-between">
        <Text variant="bodyS" bold noWrap>
          ID
        </Text>
        <Text variant="monospace" ellipsis align="right">
          {(message as WebSocketFeedbackMessage).data.message_id ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (message as any).data.id}
        </Text>
      </Flex>
    );
  }

  if (channel === WebSocketChannel.ACTION) {
    return (
      <Flex direction="column" gap="xs">
        <Flex gap="m" align="center" justify="space-between">
          <Text variant="bodyS" bold noWrap>
            Type
          </Text>
          <Text variant="monospace" ellipsis align="right">
            {(message as WebSocketActionMessage).data.type}
          </Text>
        </Flex>
        <Flex gap="m" align="center" justify="space-between">
          <Text variant="bodyS" bold noWrap>
            Payload
          </Text>
          <Text variant="monospace" ellipsis align="right">
            {JSON.stringify(
              (message as WebSocketActionMessage).data.payload,
              null,
              2,
            )}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return "";
};

/**
 * Channels that should be excluded from debug logging
 */
export const DEBUG_EXCLUDED_CHANNELS = [WebSocketChannel.PING];

/**
 * Channels that should show visual debug snackbars for incoming messages
 */
export const DEBUG_VISUAL_INCOMING_CHANNELS = [
  WebSocketChannel.CHAT,
  WebSocketChannel.CHAT_INITIATE,
  WebSocketChannel.CHAT_TEMPLATES,
  WebSocketChannel.CHAT_FEEDBACK,
  WebSocketChannel.ACTION,
];

/**
 * Channels that should show visual debug snackbars for outgoing messages
 */
export const DEBUG_VISUAL_OUTGOING_CHANNELS = [
  WebSocketChannel.CHAT,
  WebSocketChannel.CHAT_INITIATE,
  WebSocketChannel.CHAT_TEMPLATES,
  WebSocketChannel.CHAT_FEEDBACK,
  WebSocketChannel.ACTION,
];

/**
 * Channels that should be excluded from console logging
 */
export const DEBUG_CONSOLE_EXCLUDED_CHANNELS = [WebSocketChannel.AUDIO_BLOB];
